// Kjuup Pro auth pieces (mobile src/components/register/v2/pro/authPro.js, design KPAuth.dc.html
// Sign in 2b), shared by Sign in and Register: a plain `bg` page with the back chevron (or the logo)
// and the language chip in the head row, a 30/36 bold title over a 16/24 muted subline, 52 fields
// with a 13 semibold label, 54 / 50 buttons (radius 14), text links in `primaryText` and the "or"
// divider. Logged out = the fallback package (Kjuup orange), as on mobile. Presentation only: the
// pages keep state, requests and navigation.
import React from "react";
import { SUPPORTED_LANGUAGES } from "./authLanguages";
import "./authPro.css";

const Icon = ({ name, fill, className = "" }) => (
  <span
    className={`material-symbols-outlined kj-auth__icon${fill ? " kj-auth__icon--fill" : ""} ${className}`}
    aria-hidden="true"
  >
    {name}
  </span>
);

/** The page: centred column on desktop; on a phone-width screen the column fills the height so an
 *  <AuthSpacer /> pins what follows it (the CTA) to the bottom, like the app. */
export function AuthFrame({ children, testId, pkg }) {
  // pkg 'io' = the Kjuup.io highlight (purple) instead of the fallback orange.
  return (
    <div className={`kj-auth${pkg === "io" ? " kj-auth--io" : ""}`} data-testid={testId}>
      <div className="kj-auth__col">{children}</div>
    </div>
  );
}

/** Head row: back chevron (or the logo when there is no way back) on the left, language chip on the right. */
export function AuthHead({ onBack, backLabel = "Back", logo, language }) {
  return (
    <div className="kj-auth__head">
      {onBack ? (
        <button type="button" className="kj-auth__back" onClick={onBack} aria-label={backLabel} title={backLabel}>
          <Icon name="keyboard_arrow_left" />
        </button>
      ) : logo ? (
        <img className="kj-auth__logo" src={logo} alt="Kjuup" />
      ) : (
        <span />
      )}
      {language ? <AuthLanguageChip {...language} /> : null}
    </div>
  );
}

/** Language chip (mobile AuthLanguagePicker): globe + the language's own name + chevron. A native
 *  select lies invisibly over the chip, so the pick uses the browser's own list and keyboard. */
export function AuthLanguageChip({ code, onChange, label = "Language" }) {
  const current = SUPPORTED_LANGUAGES.find(l => l.code === code) || SUPPORTED_LANGUAGES[0];
  return (
    <label className="kj-auth__lang">
      <Icon name="language" className="kj-auth__lang-icon" />
      <span className="kj-auth__lang-text">{current.nativeName}</span>
      <Icon name="keyboard_arrow_down" className="kj-auth__lang-icon" />
      <select
        className="kj-auth__lang-select"
        value={current.code}
        onChange={e => onChange && onChange(e.target.value)}
        aria-label={label}
      >
        {SUPPORTED_LANGUAGES.map(lang => (
          <option key={lang.code} value={lang.code}>
            {lang.nativeName}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Title block: bold 30/36 title, muted 16/24 subline. */
export function AuthTitle({ title, sub }) {
  return (
    <div className="kj-auth__title-block">
      <div className="kj-auth__title" role="heading" aria-level={1}>
        {title}
      </div>
      {sub ? <p className="kj-auth__sub">{sub}</p> : null}
    </div>
  );
}

/** The line under a field: `error` glyph + 13 semibold in `err`; tone 'help' = muted, no glyph. */
export function AuthFieldMessage({ text, tone = "error", id }) {
  if (!text) return null;
  const help = tone === "help";
  return (
    <div className={`kj-auth__msg${help ? " kj-auth__msg--help" : ""}`} id={id} role={help ? undefined : "alert"}>
      {help ? null : <Icon name="error" fill />}
      <span>{text}</span>
    </div>
  );
}

/** Field: 13 semibold label over a 52 box (radius 14, 1.5 `line` border, `err` when invalid), a 22
 *  leading glyph in `muted`, optional trailing button and the message line. */
export function AuthInput({ id, label, icon, error, message, trailing, inputRef, ...rest }) {
  const msgId = message ? `${id}-msg` : undefined;
  return (
    <div className="kj-auth__field">
      {label ? (
        <label className="kj-auth__label" htmlFor={id}>
          {label}
        </label>
      ) : null}
      <div className={`kj-auth__box${error ? " kj-auth__box--error" : ""}${trailing ? " kj-auth__box--trailing" : ""}`}>
        {icon ? <Icon name={icon} /> : null}
        <input
          id={id}
          ref={inputRef}
          className="kj-auth__input"
          aria-invalid={error ? true : undefined}
          aria-describedby={msgId}
          {...rest}
        />
        {trailing}
      </div>
      <AuthFieldMessage id={msgId} text={message} tone={error ? "error" : "help"} />
    </div>
  );
}

/** Show / hide button of a password field (40×40, `visibility` / `visibility_off` 22 muted). */
export function AuthEye({ shown, onToggle, showLabel = "Show password", hideLabel = "Hide password" }) {
  const label = shown ? hideLabel : showLabel;
  return (
    <button type="button" className="kj-auth__eye" onClick={onToggle} aria-label={label} title={label}>
      <Icon name={shown ? "visibility_off" : "visibility"} />
    </button>
  );
}

/** Notice in the page (radius 14, tone fill, 20 glyph in the tone colour, 14/20 ink text). */
export function AuthNotice({ tone = "warn", icon = "info", text, children }) {
  return (
    <div className={`kj-auth__notice kj-auth__notice--${tone}`} role="status" aria-live="polite">
      <Icon name={icon} fill />
      <div className="kj-auth__notice-body">
        {text ? <div className="kj-auth__notice-text">{text}</div> : null}
        {children}
      </div>
    </div>
  );
}

/** "or" divider: muted lowercase label between two `line` rules. */
export function AuthOr({ label }) {
  return (
    <div className="kj-auth__or">
      <span>{String(label || "").toLowerCase()}</span>
    </div>
  );
}

/** Takes the free height on a phone-width screen, pinning what follows to the bottom. */
export function AuthSpacer() {
  return <div className="kj-auth__spacer" />;
}
